/**
 *  @class
 *  @function AddToCartButton
 */

class AddToCartButton extends HTMLElement {
	constructor() {
		super();
		this.cartData = null;
		this.classes = {
			plus: 'plus',
			minus: 'minus',
			active: 'active',
			openCC: 'open-cc',
			sidePanelClose: '.side-panel-close',
		};

		this.selectors = {
			drawerToggleItemCount: '.thb-item-count',
			drawerCheckoutButton: '.cart-drawer__checkout-button',
			cartDrawerId: 'Cart-Drawer',
		};
	}

	/**
	 * Opens the cart drawer and optionally updates its content. Safe to call from any add/change flow.
	 * @param {string|null} sectionsResponseText - Optional JSON string (from cart add/change/update) containing sections to render
	 */
	openCartDrawer(sectionsResponseText = null) {
		if (this.cartData && typeof window.getCartData === 'function') {
			window.getCartData();
		}

		if (window.location.pathname === '/cart') {
			window.location.reload();
			return;
		}

		if (sectionsResponseText) {
			this.updateCartDrawerUI(sectionsResponseText);
		}

		const checkoutBtn = document.querySelector(this.selectors.drawerCheckoutButton);
		if (checkoutBtn) checkoutBtn.href = '/checkout';

		const drawer =
			(window.CartDrawer && window.CartDrawer.container) ||
			document.getElementById(this.selectors.cartDrawerId);
		if (drawer) {
			document.body.classList.add(this.classes.openCC);
			drawer.classList.add(this.classes.active);
			drawer.focus();
		}
	}

	connectedCallback() {
		if (this.hasAttribute('data-initialized')) return;
		this.setAttribute('data-initialized', '');

		this.addEventListener('click', this.handleClick.bind(this));
	}

	async handleClick(e) {
		e.preventDefault();
		const button = e.currentTarget;

		if (!button.classList.contains(this.classes.plus) && !button.classList.contains(this.classes.minus)) {
			return;
		}

		this.setLoading(button, true);

		if (button.dataset.quickView === 'true') {
			await this.quickView(button);
		} else {
			await this.addToCartTheOnlyVariant(button);
		}

		this.setLoading(button, false);
	}

	async quickView(button) {
		const drawer = document.getElementById('Product-Drawer');
		const body = document.body;
		const productHandle = button.dataset.productHandle;
		let href = `${theme.routes.root_url}/products/${productHandle}?view=quick-view`.replace('//', '/');

		if (!href || !productHandle) return;

		try {
			const response = await fetch(href);
			const text = await response.text();
			const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html').querySelector('#Product-Drawer-Content').innerHTML;
			this.renderQuickview(sectionInnerHTML, drawer, body);
		} catch (error) {
			this.setLoading(button, false);
		}
	}

	renderQuickview(sectionInnerHTML, drawer, body) {
		if (sectionInnerHTML) {
			drawer.querySelector('#Product-Drawer-Content').innerHTML = sectionInnerHTML;

			// Ensure product-form custom element is defined so form doesn't native-submit to /cart (scripts in innerHTML don't run)
			const productFormScript = drawer.dataset.productFormScript;
			if (productFormScript && typeof loadScriptOnce === 'function') {
				const hasLegacyForm = drawer.querySelector('product-form') && !customElements.get('product-form');
				const hasNewForm = drawer.querySelector('product-form-new') && !customElements.get('product-form-new');

				if (hasLegacyForm || hasNewForm) {
					loadScriptOnce(productFormScript);
				}
			}

			let quantity = drawer.querySelector('.quantity:not(.buttons_added)');
			if (quantity) {
				new QuantityInput(quantity);
			}

			body.classList.add(this.classes.openCC);
			drawer.classList.add(this.classes.active);
			drawer.querySelector(this.selectors.sidePanelClose).focus();
		}
	}

	async addToCartTheOnlyVariant(button) {
		try {
			const handle = button.dataset.handle;
			const key = button.dataset.key;

			if (handle) {
				await this.handleByVariantId(button, handle);
			} else if (key) {
				await this.handleByKey(button, key);
			} else {
				throw new Error('No handle or key found');
			}
		} catch (error) {
			console.error(error);
		} finally {
			this.setLoading(button, false);
		}
	}

	setLoading(button, isLoading) {
		if (button) {
			if (isLoading) {
				button.classList.add('loading');
			} else {
				button.classList.remove('loading');
			}
		}
	}

	async handleByVariantId(button, handle) {
		const variantId = button.dataset.variantId;
		if (!variantId) return;

		if (button.classList.contains(this.classes.plus)) {
			await this.addItemByVariantId(button, variantId);
		} else if (button.classList.contains(this.classes.minus)) {
			await this.removeItemByVariantId(button, variantId);
		}
	}

	async handleByKey(button, key) {
		if (!this.cartData) {
			await this.updateCartData();
		}

		if (button.classList.contains(this.classes.plus)) {
			await this.increaseItemByKey(button, key);
		} else if (button.classList.contains(this.classes.minus)) {
			await this.decreaseItemByKey(button, key);
		}
	}

	async addItemByVariantId(button, variantId) {
		const sections = window.location.pathname === '/cart'
			? 'cart-drawer,cart-bubble,main-cart'
			: 'cart-drawer,cart-bubble';
		const formData = {
			sections,
			sections_url: window.location.pathname,
			items: [
				{
					id: variantId,
					quantity: 1,
				},
			],
		};

		if (button.closest('[data-srcl="coltabs"]')) {
			formData.items[0].properties = {
				_srcl: `coltabs_${window.newVariantOfABTest ? 'B' : 'A'}`,
			};
		}

		const response = await fetch('/cart/add.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(formData),
		});

		const responseText = await response.clone().text();
		let respJson;
		try {
			respJson = JSON.parse(responseText);
		} catch {
			return;
		}

		if (respJson.status) {
			return;
		}

		// Use sections from add response when present to avoid extra request; otherwise fetch full cart
		const hasSections = respJson.sections && typeof respJson.sections === 'object';
		if (hasSections) {
			this.cartData = await fetch('/cart.js').then((r) => r.json());
			if (window.location.pathname === '/cart' && typeof Cart !== 'undefined' && Cart.applySectionsFromResponse) {
				Cart.applySectionsFromResponse(respJson);
			}
			if (typeof window.getCartData === 'function') {
				await window.getCartData();
			}
			this.openCartDrawer(responseText);
		} else {
			await this.updateCartData();
			this.openCartDrawer();
		}
		this.trackAnalytics();
	}

	async removeItemByVariantId(button, variantId) {
		if (!this.cartData) {
			await this.updateCartData();
		}

		const cartVariant = this.cartData.items.find((item) => item.variant_id === parseInt(variantId));

		if (!cartVariant) return;

		const formData = {
			sections: 'cart-drawer,cart-bubble',
			sections_url: window.location.pathname,
			id: cartVariant.key,
			quantity: cartVariant.quantity - 1,
		};

		const response = await fetch('/cart/change.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(formData),
		});

		const respJson = await response.clone().json();
		const responseText = await response.clone().text();

		if (respJson.status) {
			return;
		}

		this.cartData = respJson;
		if (typeof window.getCartData === 'function') {
			await window.getCartData();
		}
		this.openCartDrawer(responseText);
	}

	async increaseItemByKey(button, key) {
		const cartVariant = this.cartData.items.find((item) => item.key === key);

		if (!cartVariant) return;

		const formData = {
			sections: 'cart-drawer,cart-bubble',
			sections_url: window.location.pathname,
			id: cartVariant.key,
			quantity: cartVariant.quantity + 1,
		};

		if (button.closest('[data-srcl="coltabs"]')) {
			formData.properties = {
				_srcl: 'coltabs',
			};
		}

		const response = await fetch('/cart/change.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(formData),
		});

		const respJson = await response.clone().json();
		const responseText = await response.clone().text();

		if (respJson.status) {
			return;
		}

		this.cartData = respJson;
		if (typeof window.getCartData === 'function') {
			await window.getCartData();
		}
		this.openCartDrawer(responseText);
		this.trackAnalytics();
	}

	async decreaseItemByKey(button, key) {
		const cartVariant = this.cartData.items.find((item) => item.key === key);

		if (!cartVariant) return;

		const formData = {
			sections: 'cart-drawer,cart-bubble',
			sections_url: window.location.pathname,
			id: cartVariant.key,
			quantity: cartVariant.quantity - 1,
		};

		const response = await fetch('/cart/change.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(formData),
		});

		const respJson = await response.clone().json();
		const responseText = await response.clone().text();

		if (respJson.status) {
			return;
		}

		this.cartData = respJson;
		if (typeof window.getCartData === 'function') {
			await window.getCartData();
		}
		this.openCartDrawer(responseText);
	}

	async updateCartPage() {
		if (this.cartData) {
			document.querySelectorAll(this.selectors.drawerToggleItemCount).forEach((el) => {
				el.innerHTML = this.cartData.item_count;
			});
		}
	}

	updateCartDrawerUI(stateText) {
		try {
			const parsedState = JSON.parse(stateText);

			if (!parsedState.sections) return;

			const sectionsToRender = [
				{
					id: 'Cart-Drawer',
					section: 'cart-drawer',
					selector: '.cart-drawer',
				},
				{
					id: 'cart-drawer-toggle',
					section: 'cart-bubble',
					selector: '.thb-item-count',
				},
			];

			sectionsToRender.forEach((section) => {
				const container = document.getElementById(section.id);
				if (!container) return;

				const elementToReplace = container.querySelector(section.selector) || container;
				const sectionHTML = parsedState.sections[section.section];

				if (sectionHTML) {
					const parsedHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
					const newContent = parsedHTML.querySelector(section.selector) || parsedHTML.body.firstElementChild;

					if (newContent) {
						elementToReplace.innerHTML = newContent.innerHTML;
					}
				}

				// Reinitialize quantity inputs
				const quantities = elementToReplace.querySelectorAll('.quantity:not(.buttons_added)');
				if (quantities.length > 0) {
					quantities.forEach((quantity) => {
						new QuantityInput(quantity);
					});
				}

				// Reinitialize remove buttons
				const removes = elementToReplace.querySelectorAll('.remove');
				if (removes.length > 0 && window.CartDrawer) {
					removes.forEach((remove) => {
						remove.onclick = (event) => {
							window.CartDrawer.updateQuantity(event.target.dataset.index, '0');
							event.preventDefault();
						};
					});
				}
			});
		} catch (error) {
			console.error('Error updating cart drawer UI:', error);
		}
	}

	trackAnalytics() {
		if (!window.store?.templateType) return;

		const templateType = window.store.templateType;
		const attributeMap = {
			collection: 'Collection_atc',
			index: 'Homepage_atc',
			product: 'Product_page_atc',
		};

		const attribute = attributeMap[templateType];
		if (attribute) {
			fetch(`/cart/update.js?attributes[${attribute}]=Yes`)
				.then((response) => response.json())
				.then(() => { if (typeof window.getCartData === 'function') window.getCartData(); })
				.catch((error) => console.error(error));
		}

		if (document.body.classList.contains('template-page-vm-reviews-page')) {
			fetch('/cart/update.js?attributes[Reviews_page_atc]=Yes')
				.then((response) => response.json())
				.then(() => { if (typeof window.getCartData === 'function') window.getCartData(); })
				.catch((error) => console.error(error));
		}
	}

	async updateCartData(callback, skipCartDrawer = false) {
		const data = await fetch('/cart/update.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				items: [],
				sections: 'cart-drawer,cart-bubble',
				sections_url: window.location.pathname,
			}),
		});

		const response = await data.clone().text();
		const responseJson = await data.clone().json();

		this.cartData = responseJson;

		if (!skipCartDrawer) {
			this.updateCartDrawerUI(response);
		}

		const checkoutButton = document.querySelector(this.selectors.drawerCheckoutButton);
		if (checkoutButton) {
			checkoutButton.href = '/checkout';
		}

		if (typeof window.getCartData === 'function') {
			window.getCartData();
		}

		if (callback) {
			callback();
		}
	}
}

if (!customElements.get('add-to-cart-button')) {
	customElements.define('add-to-cart-button', AddToCartButton);
}